import { responseInCut, stripLabelQuotes } from '@/components/quick-study-results/quickStudyPure'
import { ExhibitSpec, Result, TwinResponse } from '@/components/quick-study-results/quickStudyTypes'

import { shortOptionLabel } from '../shortOptionLabel'
import { responseItemId } from './highlightMagazineStripPersonas'

export interface StudyDocStripLookup {
  id: string
  questionId?: string
}

/**
 * Which face the band wears (D1). `kpi` when the featured result is a single/multiple
 * choice question that yields a computable top-option share; `quotes` otherwise (open
 * ends, ratings, matrices, and any choice question whose respondents never answered).
 */
export type MagazineBandMode = 'kpi' | 'quotes'

/**
 * The KPI headline the band renders (D1/D4). `pct` is the top option's share of the
 * respondents who answered; `label` is the option label VERBATIM from the survey (never
 * LLM copy). `count`/`total` are respondent counts deduped by `persona_id`, so the
 * sub-line reads "59 of 72 respondents" even on a monadic result whose rows repeat per
 * item. `memberPersonaIds` is the lit set:
 *   - `membership: 'derived'` — real membership, the respondents whose own answer was the
 *     top option, so "lit faces are the N%" is literally true.
 *   - `membership: 'filled'`  — per-respondent answers were absent and the share came from
 *     an aggregate distribution; the lit set is a deterministic, seeded subset of the same
 *     size, illustrative rather than factual.
 */
export interface MagazineBandKpi {
  pct: number
  label: string
  count: number
  total: number
  memberPersonaIds?: Array<string>
  membership: 'derived' | 'filled'
}

export interface MagazineBandSource {
  title: string
  standfirst: string
  responses: Array<TwinResponse>
  mode: MagazineBandMode
  kpi?: MagazineBandKpi
  featuredQuestionName?: string
  activeCutIds?: Array<string>
}

function isStudyDocResult(value: unknown): value is Result {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as Record<string, unknown>
  return typeof candidate.plot === 'string' && typeof candidate.title === 'string'
}

function stripLookupKeys(stripId: string): Array<string> {
  const id = stripId.trim()
  if (!id) {
    return []
  }
  const keys = [id]
  if (id.startsWith('quotes_strip:')) {
    keys.push(id.slice('quotes_strip:'.length))
  } else {
    keys.push(`quotes_strip:${id}`)
  }
  return keys
}

function stripQuestionId(
  stripId: string | undefined,
  strips?: Record<string, StudyDocStripLookup>,
): string {
  if (!stripId || !strips) {
    return ''
  }
  for (const key of stripLookupKeys(stripId)) {
    const questionId = (strips[key]?.questionId || '').trim()
    if (questionId) {
      return questionId
    }
  }
  return ''
}

function resultQuestionName(result: Result): string {
  const name = result.question?.name
  return typeof name === 'string' ? name.trim() : ''
}

function resultMatchesQuestion(result: Result, questionId: string): boolean {
  if (!questionId) {
    return false
  }
  const name = resultQuestionName(result)
  return name === questionId || name === `quotes_strip:${questionId}`
}

function activeCutIdsFromResult(result: Result): Array<string> | undefined {
  const spec: ExhibitSpec | undefined = result.exhibitSpec
  const raw = spec?.preAppliedCuts
  if (!Array.isArray(raw)) {
    return undefined
  }
  const cuts = raw.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
  return cuts.length > 0 ? cuts : undefined
}

function magazineTitle(result: Result): string {
  const narrative = (result.narrative_title || '').trim()
  if (narrative) {
    return narrative
  }
  const construct = (result.construct_title || '').trim()
  if (construct) {
    return construct
  }
  return (result.title || '').trim()
}

/**
 * Strip banned em/en dashes from any pass-through narrative text (global rule: no em/en
 * dash in user-facing copy). A dash used as a clause separator (" — "/" – ") becomes
 * ": "; a bare dash with no surrounding spaces is just dropped. This is a plain safety
 * net for whatever prose `result.headline`/`result.takeaway` carries — the ranking
 * standfirst below never routes through this path at all, it composes its own sentence
 * from structured per-respondent tallies (Step 5b visual QA finding 3).
 */
function sanitizeStandfirstCopy(text: string): string {
  return text
    .replace(/\s[–—]\s/g, ': ')
    .replace(/[–—]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/**
 * Composed standfirst for the featured question, in precedence order:
 *   1. ranking questions — first-place tallies ({@link rankingStandfirst});
 *   2. single/multiple choice questions — pick tallies ({@link choiceStandfirst});
 *   3. anything else — the LLM's own headline/takeaway, dash-sanitized.
 *
 * Step 5b visual QA finding 3 only covered (1); the live run that failed it was a
 * `single_select`, which fell all the way through to (3) and rendered the LLM's glued
 * full option labels. (2) closes that gap with the same composed-sentence machinery.
 */
function magazineStandfirst(
  result: Result,
  featuredQuestionName: string,
  activeCutIds: Array<string>,
): string {
  const composed =
    rankingStandfirst(result, featuredQuestionName, activeCutIds) ??
    choiceStandfirst(result, featuredQuestionName, activeCutIds)
  if (composed) {
    return composed
  }
  const headline = (result.headline || '').trim()
  if (headline) {
    return sanitizeStandfirstCopy(headline)
  }
  return sanitizeStandfirstCopy((result.takeaway || '').trim())
}

// --------------------------------------------------------------------------- //
// KPI derivation (D1 + D4). Everything below is pure and NAME-AGNOSTIC: it keys off
// the SurveyJS question TYPE and the respondent's own answer value, never off a
// question name, id, template, or a hard-coded answer (hard rule 1). Nothing here
// reuses the `qs_main` / /why|comment|open/i heuristics of
// `highlightMagazineStripPersonas`; the only shared machinery is `responseInCut`.
// --------------------------------------------------------------------------- //

/**
 * SurveyJS-native single/multiple-choice question types, plus the canonical spec
 * aliases the generator emits for the same two shapes. A KPI is only meaningful for a
 * question whose answers ARE options; every other type falls through to quotes mode.
 */
const CHOICE_QUESTION_TYPES = new Set([
  'radiogroup',
  'dropdown',
  'checkbox',
  'tagbox',
  'imagepicker',
  'single_select',
  'multi_select',
])

interface ChoiceOption {
  label: string
  /** Authoring order, the deterministic tie-break when two options tie on count. */
  order: number
}

interface ChoiceTally {
  /** Respondents (deduped by persona_id) who gave a usable answer. */
  total: number
  /** Option id → the persona ids that picked it, in response order. */
  members: Map<string, Array<string>>
  /** Every counted persona id, in first-encountered order — the exact population
   *  `total` describes (D2). Used to keep the band's tile panel in sync with the
   *  caption instead of showing every deduped persona in the result. */
  countedPersonaIds: Array<string>
}

/** A KPI derivation plus the exact respondent population its `total`/`count` describe
 *  (D2). `MagazineBandKpi` itself stays population-free — the population is an
 *  internal detail this module uses to filter `MagazineBandSource.responses` so the
 *  tile panel and the "lit faces are the N%" caption describe the same respondents. */
interface KpiDerivation {
  kpi: MagazineBandKpi
  populationPersonaIds: Array<string>
}

interface TopOption {
  optionId: string
  memberPersonaIds: Array<string>
}

function isChoiceQuestion(result: Result): boolean {
  const type = String(result.question?.type ?? '').trim().toLowerCase()
  return CHOICE_QUESTION_TYPES.has(type)
}

function personaIdFromRow(row: TwinResponse): string {
  return typeof row.persona_id === 'string' ? row.persona_id.trim() : ''
}

function rowInActiveCut(row: TwinResponse, activeCutIds: Array<string>): boolean {
  return activeCutIds.length === 0 || activeCutIds.some((cutId) => responseInCut(row, cutId))
}

/** One scalar answer value → its option id. Objects (matrix cells, "other" payloads)
 *  are not option ids and yield "", which drops the row from the tally. */
function scalarOptionId(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : ''
  }
  if (typeof value === 'boolean') {
    return String(value)
  }
  return ''
}

/** The option ids one respondent picked: one for a single choice, N for a multi. */
function answerOptionIds(value: unknown): Array<string> {
  if (value == null) {
    return []
  }
  const raw = Array.isArray(value) ? value : [value]
  const ids: Array<string> = []
  raw.forEach((entry) => {
    const id = scalarOptionId(entry)
    if (id && !ids.includes(id)) {
      ids.push(id)
    }
  })
  return ids
}

/** `question.choices` → option id → label + authoring order. Accepts the SurveyJS
 *  string form and the `{ value, text }` object form the generator emits. */
function choiceOptions(result: Result): Map<string, ChoiceOption> {
  const options = new Map<string, ChoiceOption>()
  const choices = Array.isArray(result.question?.choices) ? result.question.choices : []
  choices.forEach((choice: unknown, index: number) => {
    if (typeof choice === 'string') {
      const id = choice.trim()
      if (id && !options.has(id)) {
        options.set(id, { label: stripLabelQuotes(id) || id, order: index })
      }
      return
    }
    if (!choice || typeof choice !== 'object') {
      return
    }
    const raw = choice as Record<string, unknown>
    const id = String(raw.value ?? raw.id ?? '').trim()
    if (!id || options.has(id)) {
      return
    }
    const label = stripLabelQuotes(String(raw.text ?? raw.label ?? '')) || id
    options.set(id, { label, order: index })
  })
  return options
}

function optionLabel(optionId: string, options: Map<string, ChoiceOption>): string {
  return options.get(optionId)?.label || stripLabelQuotes(optionId) || optionId
}

function sharePct(count: number, total: number): number {
  return total > 0 ? Math.round((count * 100) / total) : 0
}

/**
 * The item a monadic result's KPI votes should be scoped to (D4/H2). A monadic result
 * pools every item's rows into one `responses` array (one row per persona per item), so
 * without scoping, each respondent's vote comes from whichever item's row the array
 * happens to order first for THEM — different respondents could end up voting on
 * different items. This counts, per `item_id`, how many in-cut respondents gave a
 * usable answer FOR THAT ITEM, and returns the most-represented one (ties break on
 * first-encountered order, then item id, for determinism) so every respondent's vote
 * comes from the SAME item. Returns "" when no row carries an `item_id` (non-monadic —
 * {@link responseItemId} is the exact extraction `highlightMagazineStripPersonas`
 * already uses, reused here verbatim; never that module's `qs_main` heuristics).
 */
function dominantItemId(
  responses: Array<TwinResponse>,
  questionName: string,
  activeCutIds: Array<string>,
): string {
  const counts = new Map<string, { count: number; order: number }>()
  let order = 0

  responses.forEach((row) => {
    if (!rowInActiveCut(row, activeCutIds)) {
      return
    }
    const optionIds = answerOptionIds((row.survey_data || {})[questionName])
    if (optionIds.length === 0) {
      return
    }
    const itemId = responseItemId(row)
    if (!itemId) {
      return
    }
    const existing = counts.get(itemId)
    if (existing) {
      existing.count += 1
    } else {
      counts.set(itemId, { count: 1, order })
      order += 1
    }
  })

  if (counts.size === 0) {
    return ''
  }
  const ranked = [...counts.entries()]
    .map(([itemId, entry]) => ({ itemId, ...entry }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        a.order - b.order ||
        (a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0),
    )
  return ranked[0].itemId
}

/**
 * Per-respondent distribution of `survey_data[questionName]` (D4). One vote per
 * `persona_id`: a monadic result repeats a respondent once per item, so only the row
 * matching `featuredItemId` counts (H2) — "" (non-monadic) skips item scoping
 * entirely and falls back to the first row carrying a usable answer.
 *
 * Generalized over `extractOptionIds` so the same item-scoped, in-cut,
 * deduped-by-persona loop tallies "every picked option" (choice questions, via
 * {@link answerOptionIds}) or "only the first-ranked option" (ranking questions, via
 * {@link firstPlaceOptionIds}) without duplicating it.
 */
function tallyAnswers(
  responses: Array<TwinResponse>,
  questionName: string,
  activeCutIds: Array<string>,
  featuredItemId: string,
  extractOptionIds: (value: unknown) => Array<string>,
): ChoiceTally {
  const members = new Map<string, Array<string>>()
  const counted = new Set<string>()
  const countedPersonaIds: Array<string> = []
  let total = 0

  responses.forEach((row) => {
    const personaId = personaIdFromRow(row)
    if (!personaId || counted.has(personaId)) {
      return
    }
    if (!rowInActiveCut(row, activeCutIds)) {
      return
    }
    if (featuredItemId && responseItemId(row) !== featuredItemId) {
      return
    }
    const optionIds = extractOptionIds((row.survey_data || {})[questionName])
    if (optionIds.length === 0) {
      return
    }
    counted.add(personaId)
    countedPersonaIds.push(personaId)
    total += 1
    optionIds.forEach((optionId) => {
      const bucket = members.get(optionId)
      if (bucket) {
        bucket.push(personaId)
      } else {
        members.set(optionId, [personaId])
      }
    })
  })

  return { total, members, countedPersonaIds }
}

function tallyChoiceAnswers(
  responses: Array<TwinResponse>,
  questionName: string,
  activeCutIds: Array<string>,
  featuredItemId: string,
): ChoiceTally {
  return tallyAnswers(responses, questionName, activeCutIds, featuredItemId, answerOptionIds)
}

/** SurveyJS ranking question type. Kept separate from {@link CHOICE_QUESTION_TYPES}
 *  (declared further down) — a ranking's answer is an ORDER of options, not a pick, so
 *  it never feeds the KPI derivation, only the standfirst leaderboard below. */
const RANKING_QUESTION_TYPES = new Set(['ranking'])

function isRankingQuestion(result: Result): boolean {
  const type = String(result.question?.type ?? '').trim().toLowerCase()
  return RANKING_QUESTION_TYPES.has(type)
}

/** The respondent's #1 choice only — the first entry of the ranked-order array. */
function firstPlaceOptionIds(value: unknown): Array<string> {
  return answerOptionIds(value).slice(0, 1)
}

interface RankedFirstPlaceOption {
  optionId: string
  count: number
  order: number
}

/**
 * Composes a plain-sentence standfirst for a ranking question from first-place tallies
 * only — never from the LLM's own `headline`/`takeaway` prose, which is where the em/en
 * dashes and glued full option descriptions came from (Step 5b visual QA finding 3: "Too
 * close to call — Trust, Amplified — A guide to... tie at 42% (n=50)."). Name-agnostic:
 * only reads the SurveyJS question TYPE and each respondent's own first-ranked answer
 * (hard rule 1) — never a question id/name or a hard-coded option. `null` when there is
 * nothing to tally (falls back to the sanitized headline/takeaway path).
 */
function rankingStandfirst(
  result: Result,
  questionName: string,
  activeCutIds: Array<string>,
): string | null {
  if (!questionName || !isRankingQuestion(result)) {
    return null
  }
  return talliedStandfirst(result, questionName, activeCutIds, firstPlaceOptionIds)
}

/**
 * The same composed sentence for a single/multiple-choice question, tallying what each
 * respondent actually PICKED ({@link answerOptionIds}) instead of what they ranked first.
 * Step 5b visual QA finding 3 was filed against a ranking result, so only
 * {@link rankingStandfirst} shipped; the `single_select` run reviewed next fell straight
 * through to the sanitize-only headline fallback and rendered "Too close to call: Trust,
 * Amplified: A guide to using AI responsibly while... and AI Advantage: A practical
 * guide... tie at 42% (n=50)." — full option descriptions glued together. Name-agnostic
 * like the ranking path: it reads the SurveyJS question TYPE and each respondent's own
 * answer, never a question id/name or a hard-coded option (hard rule 1). `null` → fall
 * through to the headline.
 */
function choiceStandfirst(
  result: Result,
  questionName: string,
  activeCutIds: Array<string>,
): string | null {
  if (!questionName || !isChoiceQuestion(result)) {
    return null
  }
  return talliedStandfirst(result, questionName, activeCutIds, answerOptionIds)
}

/**
 * The one sentence builder both standfirst paths share. Option names always go through
 * {@link shortOptionLabel}, and the sentence itself carries no em/en dash and no
 * uppercase-shouting (global copy rules) — pinned by this module's tests.
 */
function talliedStandfirst(
  result: Result,
  questionName: string,
  activeCutIds: Array<string>,
  extractOptionIds: (value: unknown) => Array<string>,
): string | null {
  const responses = result.responses || []
  const featuredItemId = dominantItemId(responses, questionName, activeCutIds)
  const tally = tallyAnswers(
    responses,
    questionName,
    activeCutIds,
    featuredItemId,
    extractOptionIds,
  )
  if (tally.total === 0) {
    return null
  }
  const options = choiceOptions(result)
  const ranked: Array<RankedFirstPlaceOption> = [...tally.members.entries()]
    .map(([optionId, memberIds]) => ({
      optionId,
      count: memberIds.length,
      order: options.get(optionId)?.order ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort(byCountThenOrderThenId)
  const top = ranked[0]
  if (!top) {
    return null
  }
  const topLabel = shortOptionLabel(optionLabel(top.optionId, options))
  const topPct = sharePct(top.count, tally.total)
  const base = respondentCountPhrase(tally.total)
  const second = ranked[1]
  if (!second) {
    return `${topLabel} leads at ${topPct}% of ${base}.`
  }
  const secondLabel = shortOptionLabel(optionLabel(second.optionId, options))
  if (second.count === top.count) {
    return `Too close to call: ${topLabel} and ${secondLabel} tie at ${topPct}% of ${base}.`
  }
  const secondPct = sharePct(second.count, tally.total)
  return `${topLabel} leads at ${topPct}% of ${base}, followed by ${secondLabel} at ${secondPct}%.`
}

/** "50 respondents" / "1 respondent" — a one-respondent panel read as "1 respondents". */
function respondentCountPhrase(total: number): string {
  return total === 1 ? '1 respondent' : `${total} respondents`
}

/** Most picked first; ties break on authoring order, then on the option id, so the same
 *  input always yields the same headline. */
function byCountThenOrderThenId(
  a: { count: number; order: number; optionId: string },
  b: { count: number; order: number; optionId: string },
): number {
  if (a.count !== b.count) {
    return b.count - a.count
  }
  if (a.order !== b.order) {
    return a.order - b.order
  }
  return a.optionId < b.optionId ? -1 : a.optionId > b.optionId ? 1 : 0
}

/** The most-picked option of a per-respondent tally. */
function topTalliedOption(tally: ChoiceTally, options: Map<string, ChoiceOption>): TopOption | null {
  const ranked = [...tally.members.entries()]
    .map(([optionId, memberPersonaIds]) => ({
      optionId,
      memberPersonaIds,
      count: memberPersonaIds.length,
      order: options.get(optionId)?.order ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort(byCountThenOrderThenId)
  const top = ranked[0]
  return top ? { optionId: top.optionId, memberPersonaIds: top.memberPersonaIds } : null
}

/** Real membership: the respondents who themselves picked the top option. */
function derivedKpi(
  result: Result,
  questionName: string,
  activeCutIds: Array<string>,
): KpiDerivation | null {
  if (!questionName) {
    return null
  }
  const responses = result.responses || []
  const featuredItemId = dominantItemId(responses, questionName, activeCutIds)
  const tally = tallyChoiceAnswers(responses, questionName, activeCutIds, featuredItemId)
  if (tally.total === 0) {
    return null
  }
  const options = choiceOptions(result)
  const top = topTalliedOption(tally, options)
  if (!top || top.memberPersonaIds.length === 0) {
    return null
  }
  return {
    kpi: {
      pct: sharePct(top.memberPersonaIds.length, tally.total),
      label: optionLabel(top.optionId, options),
      count: top.memberPersonaIds.length,
      total: tally.total,
      memberPersonaIds: top.memberPersonaIds,
      membership: 'derived',
    },
    populationPersonaIds: tally.countedPersonaIds,
  }
}

/** FNV-1a — a stable, dependency-free seed per persona id (no RNG state, so the same
 *  panel always lights the same faces across renders and reloads). */
function personaIdSeed(personaId: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < personaId.length; index += 1) {
    hash ^= personaId.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash >>> 0
}

/** `count` persona ids picked deterministically (lowest seed first), returned in the
 *  caller's original order so tile rendering stays stable. */
function seededSubset(personaIds: Array<string>, count: number): Array<string> {
  if (count >= personaIds.length) {
    return [...personaIds]
  }
  const picked = new Set(
    [...personaIds]
      .sort((a, b) => personaIdSeed(a) - personaIdSeed(b) || (a < b ? -1 : a > b ? 1 : 0))
      .slice(0, Math.max(0, count)),
  )
  return personaIds.filter((personaId) => picked.has(personaId))
}

/** Rows whose persona id is in the KPI population (D2/H1) — keeps every row of a kept
 *  persona (so a monadic respondent's image/attributes still resolve from whichever row
 *  carries them) and only drops rows for personas the KPI denominator doesn't count. */
function responsesForPersonaIds(
  responses: Array<TwinResponse>,
  personaIds: Array<string>,
): Array<TwinResponse> {
  const keep = new Set(personaIds)
  return responses.filter((row) => keep.has(personaIdFromRow(row)))
}

function uniquePersonaIds(
  responses: Array<TwinResponse>,
  activeCutIds: Array<string>,
): Array<string> {
  const ids: Array<string> = []
  const seen = new Set<string>()
  responses.forEach((row) => {
    const personaId = personaIdFromRow(row)
    if (!personaId || seen.has(personaId) || !rowInActiveCut(row, activeCutIds)) {
      return
    }
    seen.add(personaId)
    ids.push(personaId)
  })
  return ids
}

/**
 * Pooled top-option share from the only aggregate choice distribution a Result carries
 * (`monadic_choice`, one entry per item). Counts are pooled across items and divided by
 * the pooled reads, so the share is a real measured number even though the per-respondent
 * answers behind it are gone.
 */
function aggregateTopShare(result: Result): { label: string; share: number } | null {
  const rows = Array.isArray(result.monadic_choice) ? result.monadic_choice : []
  const counts = new Map<string, { count: number; label: string; order: number }>()
  let reads = 0

  rows.forEach((row) => {
    const n = Number(row?.n)
    if (Number.isFinite(n) && n > 0) {
      reads += n
    }
    const choices = Array.isArray(row?.choices) ? row.choices : []
    choices.forEach((choice, index) => {
      const optionId = String(choice?.choice_id ?? '').trim()
      const count = Number(choice?.count)
      if (!optionId || !Number.isFinite(count) || count <= 0) {
        return
      }
      const previous = counts.get(optionId)
      counts.set(optionId, {
        count: (previous?.count ?? 0) + count,
        label: previous?.label || stripLabelQuotes(choice?.choice_label) || optionId,
        order: previous?.order ?? index,
      })
    })
  })

  if (reads <= 0 || counts.size === 0) {
    return null
  }

  const ranked = [...counts.entries()]
    .map(([optionId, entry]) => ({ optionId, ...entry }))
    .sort(byCountThenOrderThenId)
  const top = ranked[0]
  if (!top || top.count <= 0) {
    return null
  }
  return { label: top.label, share: Math.min(1, top.count / reads) }
}

/** Fallback for D4: the share is real, the lit set is a seeded stand-in of the same size. */
function filledKpi(result: Result, activeCutIds: Array<string>): KpiDerivation | null {
  const aggregate = aggregateTopShare(result)
  if (!aggregate) {
    return null
  }
  const personaIds = uniquePersonaIds(result.responses || [], activeCutIds)
  if (personaIds.length === 0) {
    return null
  }
  const total = personaIds.length
  const count = Math.min(total, Math.max(1, Math.round(aggregate.share * total)))
  return {
    kpi: {
      // `pct` is the measured aggregate share; `count`/`total` size the lit set against
      // the tiles actually on screen, so the two can differ by a rounding point.
      pct: Math.round(aggregate.share * 100),
      label: aggregate.label,
      count,
      total,
      memberPersonaIds: seededSubset(personaIds, count),
      membership: 'filled',
    },
    populationPersonaIds: personaIds,
  }
}

/**
 * D1: the band renders a KPI when the featured result is a single/multiple-choice
 * question whose distribution yields BOTH a computable top-option share and a lit set.
 * Real per-respondent membership wins; the seeded fill only runs when per-respondent
 * answers are absent. `null` → the band renders quotes. Also returns the exact
 * respondent population `kpi.total`/`kpi.count` describe (D2) — `resolveMagazineBandSource`
 * uses it to keep `MagazineBandSource.responses` in sync with the caption instead of
 * carrying every deduped persona in the result (H1).
 */
function deriveMagazineBandKpiWithPopulation(
  result: Result,
  featuredQuestionName?: string,
  activeCutIds?: Array<string>,
): KpiDerivation | null {
  if (!isChoiceQuestion(result)) {
    return null
  }
  const cutIds = (activeCutIds || []).map((cutId) => cutId.trim()).filter(Boolean)
  const questionName = (featuredQuestionName || '').trim()
  return derivedKpi(result, questionName, cutIds) ?? filledKpi(result, cutIds)
}

/**
 * Pure. Exported for unit tests and for any future caller that already holds the Result
 * and only needs the headline data (not the population — see
 * {@link deriveMagazineBandKpiWithPopulation} for that).
 */
export function deriveMagazineBandKpi(
  result: Result,
  featuredQuestionName?: string,
  activeCutIds?: Array<string>,
): MagazineBandKpi | null {
  return deriveMagazineBandKpiWithPopulation(result, featuredQuestionName, activeCutIds)?.kpi ?? null
}

export function resolveMagazineBandSource(
  plots: Record<string, unknown>,
  stripId?: string,
  strips?: Record<string, StudyDocStripLookup>,
): MagazineBandSource | null {
  const results = Object.values(plots).filter(isStudyDocResult)
  if (results.length === 0) {
    return null
  }

  const questionFromStrip = stripQuestionId(stripId, strips)
  const stripBody = (stripId || '').trim().replace(/^quotes_strip:/, '')
  const featuredHint = questionFromStrip || stripBody

  const matched =
    results.find((result) => resultMatchesQuestion(result, featuredHint)) ??
    results.find((result) => (result.responses || []).length > 0) ??
    results[0]

  if (!matched) {
    return null
  }

  const featuredQuestionName = questionFromStrip || resultQuestionName(matched) || featuredHint
  const activeCutIds = activeCutIdsFromResult(matched)
  const kpiDerivation = deriveMagazineBandKpiWithPopulation(matched, featuredQuestionName, activeCutIds)
  const responses = matched.responses || []
  const source: MagazineBandSource = {
    title: magazineTitle(matched),
    standfirst: magazineStandfirst(matched, featuredQuestionName, activeCutIds ?? []),
    // D2/H1: in kpi mode, only carry the respondents the KPI's own denominator counts
    // (in-cut + answered) — otherwise the tile panel dedupes every persona in the
    // result while the caption reads "N of <kpi.total>", and the two disagree (a
    // four-persona result with a two-in-cut KPI showed 4 tiles for a "50%" caption
    // that was really 1 of 2). Quotes mode has no KPI denominator to match, so it
    // keeps every response, unfiltered, exactly as before.
    responses: kpiDerivation
      ? responsesForPersonaIds(responses, kpiDerivation.populationPersonaIds)
      : responses,
    mode: kpiDerivation ? 'kpi' : 'quotes',
  }
  if (kpiDerivation) {
    source.kpi = kpiDerivation.kpi
  }
  if (featuredQuestionName) {
    source.featuredQuestionName = featuredQuestionName
  }
  if (activeCutIds) {
    source.activeCutIds = activeCutIds
  }
  return source
}
